import { Fragment, useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import FosterSearchItem from '@/components/search/FosterSearchItem'
import UserSearchItem from '@/components/search/UserSearchItem'
import UniDivider from '@/components/unistyles/UniDivider'
import UniFlashList from '@/components/unistyles/UniFlashList'
import UniIcon from '@/components/unistyles/UniIcon'
import UniText from '@/components/unistyles/UniText'
import { useAppStore } from '@/store/useAppStore'
import buildOrgPath from '@/utils/orgRoute'
import trpc from '@/utils/trpc'

interface Props {
  searchInput: string
}

interface SectionHeader {
  type: 'header'
  title: string
}

type SearchItem = {
  type: 'item'
  data: any
}

type ListItem = SectionHeader | SearchItem

const SearchResults = (props: Props) => {
  const { searchInput } = props
  const { theme } = useUnistyles()

  const currentOrganization = useAppStore.use.currentOrganization().organization

  const searchQuery = trpc.search.useInfiniteQuery(
    {
      organizationId: currentOrganization?.id || '',
      query: searchInput || '',
      limit: 10,
    },
    {
      enabled: !!searchInput && !!currentOrganization,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    }
  )

  const hasNoResults =
    searchQuery.data?.pages[0] &&
    searchQuery.data?.pages[0].users.length === 0 &&
    searchQuery.data?.pages[0].fosters.length === 0 &&
    !searchQuery.isLoading

  const items = useMemo(() => {
    const allUsers = searchQuery.data?.pages.flatMap((page) => page.users) || []
    const allFosters =
      searchQuery.data?.pages.flatMap((page) => page.fosters) || []

    const currentFosters = allFosters.filter(
      (f) => !f.isAdopted && !f.isDeceased
    )
    const otherFosters = allFosters.filter((f) => f.isAdopted || f.isDeceased)

    return [
      ...(allUsers.length > 0
        ? [
            { type: 'header', title: 'Volunteers' } as SectionHeader,
            ...allUsers.map((item) => ({ type: 'item' as const, data: item })),
          ]
        : []),
      ...(currentFosters.length > 0
        ? [
            { type: 'header', title: 'Current Fosters' } as SectionHeader,
            ...currentFosters.map((item) => ({
              type: 'item' as const,
              data: item,
            })),
          ]
        : []),
      ...(otherFosters.length > 0
        ? [
            { type: 'header', title: 'Fosters' } as SectionHeader,
            ...otherFosters.map((item) => ({
              type: 'item' as const,
              data: item,
            })),
          ]
        : []),
    ]
  }, [searchQuery.data?.pages])

  const renderItem = useCallback(
    ({ item, index }: { item: ListItem; index: number }) => {
      if (item.type === 'header') {
        return (
          <View>
            <UniText style={styles.sectionHeader} variant="titleSmall">
              {item.title}
            </UniText>
          </View>
        )
      }

      const isUser = 'email' in item.data
      const showDivider =
        index < items.length - 1 && items[index + 1].type !== 'header'

      return (
        <Fragment key={item.data.id + '-view'}>
          {isUser ? (
            <UserSearchItem
              fosterCount={item.data.fosterCount || 0}
              id={item.data.id}
              name={`${item.data.firstName} ${item.data.lastName}`}
              thumbUri={`https://www.gravatar.com/avatar/${item.data.emailHash}?d=identicon`}
            />
          ) : (
            <FosterSearchItem
              extraData={[
                item.data.microchipId
                  ? `Microchip: ${item.data.microchipId}`
                  : undefined,
              ]}
              id={item.data.id}
              name={item.data.name}
              path={buildOrgPath(`/fosters/${item.data.id}`)}
              thumbUri={
                item.data.blurhash
                  ? item.data.thumbUri
                  : item.data.species === 'Cat'
                    ? require('../../../assets/cat.png')
                    : require('../../../assets/dog.png')
              }
              userFullName={item.data.userFullName}
            />
          )}
          {showDivider && <UniDivider />}
        </Fragment>
      )
    },
    [items, styles.sectionHeader]
  )

  if (hasNoResults) {
    return (
      <View style={styles.emptyState}>
        <View style={styles.emptyIconContainer}>
          <UniIcon
            color={theme.colors.primary}
            size={48}
            source="magnify-close"
          />
        </View>
        <UniText style={styles.emptyTitle} variant="headlineSmall">
          No results found
        </UniText>
        <UniText style={styles.emptyText} variant="bodyMedium">
          Try adjusting your search terms or using a different query
        </UniText>
      </View>
    )
  }

  return (
    <UniFlashList
      data={items}
      ListEmptyComponent={
        searchInput && searchQuery.isLoading ? (
          <View style={styles.emptyState}>
            <UniText>Loading...</UniText>
          </View>
        ) : null
      }
      onEndReached={() => {
        if (searchQuery.hasNextPage) {
          searchQuery.fetchNextPage()
        }
      }}
      onEndReachedThreshold={0.2}
      renderItem={renderItem}
    />
  )
}

const styles = StyleSheet.create((theme) => ({
  emptyIconContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.secondaryContainer,
    borderRadius: 60,
    height: 120,
    justifyContent: 'center',
    marginBottom: theme.tokens.spacing[6],
    marginTop: theme.tokens.spacing[4],
    width: 120,
  },
  emptyState: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: theme.tokens.spacing[8],
  },
  emptyText: {
    color: theme.colors.onSurfaceVariant,
    marginTop: theme.tokens.spacing[2],
    textAlign: 'center',
  },
  emptyTitle: {
    color: theme.colors.onSurface,
    fontWeight: 'bold',
    marginBottom: theme.tokens.spacing[2],
  },
  sectionHeader: {
    color: theme.colors.onSurfaceVariant,
    paddingBottom: theme.tokens.spacing[2],
    paddingHorizontal: theme.tokens.spacing[2],
    paddingTop: theme.tokens.spacing[4],
  },
}))

export default SearchResults
